import { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import {
  Loader2, RefreshCw, Mail, Paperclip, AlertTriangle, ExternalLink,
  Filter, ChevronDown, ChevronRight, Search, BadgeCheck, Forward, Inbox,
} from "lucide-react";
import {
  fetchInboundLog, fetchLlmUsage, scanStorePrefixMismatch, reassignByStorePrefix,
  INTAKE_DECISION_COLORS, INTAKE_PRIORITY_COLORS,
} from "../../../lib/emailIntake";

const fmtDate = (iso) => {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleString("en-US", {
      month: "short", day: "2-digit", hour: "2-digit", minute: "2-digit",
    });
  } catch { return iso; }
};
const fmtUSD = (n) => Number(n || 0).toLocaleString("en-US", { style: "currency", currency: "USD" });

const DecisionPill = ({ decision }) => {
  const c = INTAKE_DECISION_COLORS[decision] || {
    bg: "#FEF3C7", fg: "#92400E", border: "#F59E0B", label: decision || "—",
  };
  return (
    <span
      className="px-1.5 py-0.5 text-[10px] font-bold uppercase tracking-[0.10em] rounded-sm whitespace-nowrap inline-flex items-center"
      style={{ background: c.bg, color: c.fg, border: `1px solid ${c.border}` }}
      data-testid={`intake-decision-${decision}`}
    >
      {c.label}
    </span>
  );
};

const PriorityPill = ({ value }) => {
  if (!value) return null;
  const c = INTAKE_PRIORITY_COLORS[value] || INTAKE_PRIORITY_COLORS.Normal;
  return (
    <span
      className="px-1.5 py-0.5 text-[10px] font-bold uppercase tracking-[0.08em] rounded-sm"
      style={{ background: c.bg, color: c.fg }}
    >
      {value}
    </span>
  );
};

const StatCard = ({ label, value, accent = "var(--ups-gold)", icon: Icon }) => (
  <div className="card-swiss p-4 rounded-sm">
    <div className="flex items-center justify-between">
      <div className="overline">{label}</div>
      {Icon && (
        <div
          className="w-6 h-6 flex items-center justify-center"
          style={{ background: accent, color: "var(--ups-ink)" }}
        >
          <Icon size={12} strokeWidth={2.5} />
        </div>
      )}
    </div>
    <div className="mt-1 font-display text-2xl font-black tracking-tight">{value}</div>
  </div>
);

/* ===== Cleanup tools (store-prefix mismatches) ===== */
const CleanupTools = () => {
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);
  const [pickedJobIds, setPickedJobIds] = useState(new Set());

  const scan = async () => {
    setBusy(true);
    try {
      const r = await scanStorePrefixMismatch();
      setResult(r);
      setPickedJobIds(new Set());
      toast.success(`Scan complete — ${r.count} mismatched jobs`);
    } catch {
      toast.error("Scan failed");
    } finally {
      setBusy(false);
    }
  };

  const reassign = async (mode) => {
    if (!result || result.count === 0) return;
    const job_ids = mode === "all"
      ? result.mismatches.map((m) => m.job_id)
      : Array.from(pickedJobIds);
    if (job_ids.length === 0) {
      toast.error("Pick at least one job, or use Reassign All.");
      return;
    }
    if (!window.confirm(`Reassign ${job_ids.length} job(s) to the store number found in their name?\nThis cannot be undone automatically.`)) return;
    setBusy(true);
    try {
      const r = await reassignByStorePrefix({ job_ids });
      toast.success(`Moved ${r.moved} job(s). Skipped ${r.skipped}.`);
      await scan();
    } catch {
      toast.error("Reassign failed");
    } finally {
      setBusy(false);
    }
  };

  const toggle = (id) => {
    setPickedJobIds((prev) => {
      const n = new Set(prev);
      if (n.has(id)) n.delete(id); else n.add(id);
      return n;
    });
  };

  return (
    <div className="card-swiss p-4 rounded-sm" data-testid="intake-cleanup-tools">
      <div className="flex items-center justify-between gap-3 mb-3">
        <div>
          <div className="overline">Data Hygiene</div>
          <div className="font-display font-bold text-sm mt-0.5">
            Store-prefix mismatch scan
          </div>
          <p className="text-xs text-[color:var(--ups-subtext)] mt-1 max-w-xl">
            Finds UPS-store jobs whose <code>customer_email</code> doesn't match the 4-digit store number in the job name
            (e.g. <code>"4802 - Flyers"</code> wrongly assigned to <code>store1140</code>). Read-only scan; you choose which to move.
          </p>
        </div>
        <button
          onClick={scan}
          disabled={busy}
          className="btn-ink px-3 py-2 rounded-sm flex items-center gap-2 text-sm shrink-0"
          data-testid="intake-scan-button"
        >
          {busy ? <Loader2 size={14} className="animate-spin" /> : <Search size={14} />}
          Run scan
        </button>
      </div>

      {result && (
        <>
          {result.count === 0 ? (
            <div
              className="text-sm py-3 px-3 rounded-sm flex items-center gap-2"
              style={{ background: "#D1FAE5", color: "#064E3B", border: "1px solid #10B981" }}
              data-testid="intake-scan-clean"
            >
              <BadgeCheck size={14} /> All UPS-store jobs match their email prefix. Nothing to clean up.
            </div>
          ) : (
            <>
              <div className="flex items-center justify-between gap-3 mb-2 mt-3">
                <div className="text-xs font-bold uppercase tracking-[0.12em]">
                  {result.count} mismatch{result.count === 1 ? "" : "es"} · {pickedJobIds.size} selected
                </div>
                <div className="flex gap-2">
                  <button
                    onClick={() => reassign("selected")}
                    disabled={busy || pickedJobIds.size === 0}
                    className="btn-ink px-3 py-1.5 rounded-sm text-xs disabled:opacity-40"
                    data-testid="intake-reassign-selected"
                  >
                    Reassign selected
                  </button>
                  <button
                    onClick={() => reassign("all")}
                    disabled={busy}
                    className="btn-brutal px-3 py-1.5 rounded-sm text-xs disabled:opacity-40"
                    data-testid="intake-reassign-all"
                  >
                    Reassign all
                  </button>
                </div>
              </div>
              <div className="border" style={{ borderColor: "var(--ups-border)" }}>
                <div
                  className="grid text-[10px] uppercase font-bold tracking-[0.12em] py-2 px-3 border-b"
                  style={{
                    gridTemplateColumns: "32px 1fr 1.5fr 0.8fr 0.8fr 0.8fr",
                    background: "var(--ups-muted)",
                    borderColor: "var(--ups-border)",
                  }}
                >
                  <div></div>
                  <div>Job</div>
                  <div>Currently → Suggested</div>
                  <div>Status</div>
                  <div>Received</div>
                  <div>Target customer</div>
                </div>
                {result.mismatches.map((m) => (
                  <label
                    key={m.job_id}
                    className="grid text-xs py-2 px-3 border-b hover:bg-[color:var(--ups-muted)] cursor-pointer"
                    style={{
                      gridTemplateColumns: "32px 1fr 1.5fr 0.8fr 0.8fr 0.8fr",
                      borderColor: "var(--ups-border)",
                    }}
                    data-testid={`intake-mismatch-row-${m.job_id}`}
                  >
                    <input
                      type="checkbox"
                      checked={pickedJobIds.has(m.job_id)}
                      onChange={() => toggle(m.job_id)}
                      data-testid={`intake-mismatch-check-${m.job_id}`}
                    />
                    <div className="font-mono text-[11px] truncate">
                      <div className="font-bold">#{m.job_number || "—"}</div>
                      <div className="text-[color:var(--ups-subtext)] truncate">{m.name}</div>
                    </div>
                    <div className="font-mono text-[11px] self-center">
                      <span className="text-red-700">{m.current_email}</span>
                      <span className="mx-1">→</span>
                      <span className="text-emerald-700">{m.suggested_email}</span>
                    </div>
                    <div className="self-center font-mono text-[11px]">{m.status || "—"}</div>
                    <div className="self-center font-mono text-[11px] text-[color:var(--ups-subtext)]">
                      {fmtDate(m.received_at)}
                    </div>
                    <div className="self-center text-[11px]">
                      {m.target_customer_exists ? (
                        <span className="font-mono text-emerald-700">exists</span>
                      ) : (
                        <span className="font-mono text-amber-700">will create</span>
                      )}
                    </div>
                  </label>
                ))}
              </div>
            </>
          )}
        </>
      )}
    </div>
  );
};

/* ===== Main intake tab ===== */
const EmailIntakeTab = () => {
  const [log, setLog] = useState([]);
  const [usage, setUsage] = useState(null);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("all"); // all | created | threaded | duplicate | discarded | forwarded
  const [query, setQuery] = useState("");
  const [openId, setOpenId] = useState(null);

  const refresh = async () => {
    setLoading(true);
    try {
      const [l, u] = await Promise.all([fetchInboundLog(200), fetchLlmUsage().catch(() => null)]);
      setLog(Array.isArray(l) ? l : []);
      setUsage(u);
    } catch {
      toast.error("Failed to load inbound log");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { refresh(); }, []);

  const counts = useMemo(() => {
    const c = { all: log.length, created: 0, threaded: 0, duplicate: 0, discarded: 0, forwarded: 0 };
    for (const e of log) {
      if (e.decision === "created") c.created++;
      else if (e.decision === "threaded") c.threaded++;
      else if (e.decision === "duplicate") c.duplicate++;
      else if ((e.decision || "").startsWith("discarded")) c.discarded++;
      if (e.is_forwarded) c.forwarded++;
    }
    return c;
  }, [log]);

  const filtered = useMemo(() => {
    let rows = log;
    if (filter === "created") rows = rows.filter((r) => r.decision === "created");
    else if (filter === "threaded") rows = rows.filter((r) => r.decision === "threaded");
    else if (filter === "duplicate") rows = rows.filter((r) => r.decision === "duplicate");
    else if (filter === "discarded") rows = rows.filter((r) => (r.decision || "").startsWith("discarded"));
    else if (filter === "forwarded") rows = rows.filter((r) => r.is_forwarded);

    const q = query.trim().toLowerCase();
    if (q) {
      rows = rows.filter((r) => {
        const hay = [
          r.from, r.subject, r.forwarded_by,
          (r.to || []).join(" "), (r.cc || []).join(" "),
        ].join(" ").toLowerCase();
        return hay.includes(q);
      });
    }
    return rows;
  }, [log, filter, query]);

  const pills = [
    { id: "all",       label: "All",       count: counts.all },
    { id: "created",   label: "Created",   count: counts.created },
    { id: "threaded",  label: "Threaded",  count: counts.threaded },
    { id: "duplicate", label: "Duplicate", count: counts.duplicate },
    { id: "discarded", label: "Discarded", count: counts.discarded },
    { id: "forwarded", label: "Forwarded", count: counts.forwarded },
  ];

  const openJobInTracker = (jobId) => {
    if (!jobId) {
      toast.error("This inbound was not linked to a job.");
      return;
    }
    // Copy the job id so the admin can paste it into the tracker search,
    // then ask the parent panel to switch tabs.
    try {
      navigator.clipboard?.writeText(jobId);
    } catch { /* no-op */ }
    window.dispatchEvent(new CustomEvent("admin:switch-tab", { detail: { tab: "tracker", jobId } }));
    toast.success("Switched to Tracker. Job id copied to clipboard.");
  };

  return (
    <div className="space-y-4" data-testid="email-intake-tab">
      {/* LLM usage strip */}
      {usage && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3" data-testid="intake-llm-usage">
          <StatCard label="Inbound this view" value={log.length} icon={Inbox} />
          <StatCard label="LLM cost (this month)" value={fmtUSD(usage.total_usd || 0)} icon={BadgeCheck} accent="#16A34A" />
          <StatCard label="LLM cap" value={fmtUSD(usage.monthly_cap_usd || 10)} accent="var(--ups-espresso)" />
          <StatCard label="Triages this month" value={(usage.total_calls || usage.by_feature && Object.values(usage.by_feature).reduce((a, b) => a + (b?.calls || 0), 0)) || 0} icon={Mail} />
        </div>
      )}

      {/* Cleanup tools */}
      <CleanupTools />

      {/* Filter pills + search */}
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div className="flex gap-1 flex-wrap" data-testid="intake-filter-pills">
          {pills.map((p) => {
            const active = filter === p.id;
            return (
              <button
                key={p.id}
                onClick={() => setFilter(p.id)}
                className="px-3 py-1.5 text-xs font-bold uppercase tracking-[0.12em] rounded-sm flex items-center gap-1.5 border-2 transition"
                style={{
                  background: active ? "var(--ups-ink)" : "transparent",
                  color: active ? "#FFB500" : "var(--ups-ink)",
                  borderColor: active ? "var(--ups-ink)" : "var(--ups-border)",
                }}
                data-testid={`intake-filter-${p.id}`}
              >
                <Filter size={11} /> {p.label}
                <span
                  className="px-1.5 py-0.5 text-[10px] rounded-sm"
                  style={{
                    background: active ? "#FFB500" : "var(--ups-muted)",
                    color: "var(--ups-ink)",
                  }}
                >
                  {p.count}
                </span>
              </button>
            );
          })}
        </div>
        <div className="flex gap-2 items-center">
          <div className="relative">
            <Search size={12} className="absolute left-2 top-1/2 -translate-y-1/2 text-[color:var(--ups-subtext)]" />
            <input
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Filter by sender or subject…"
              className="input-swiss pl-7 pr-2 py-1.5 rounded-sm text-xs w-72"
              data-testid="intake-search"
            />
          </div>
          <button
            onClick={refresh}
            className="btn-ink px-3 py-1.5 rounded-sm flex items-center gap-1.5 text-xs"
            disabled={loading}
            data-testid="intake-refresh"
          >
            <RefreshCw size={12} className={loading ? "animate-spin" : ""} /> Refresh
          </button>
        </div>
      </div>

      {/* Table */}
      <div className="card-swiss rounded-sm overflow-hidden" data-testid="intake-log-table">
        <div
          className="grid text-[11px] uppercase font-bold tracking-[0.12em] py-3 px-4 border-b"
          style={{
            gridTemplateColumns: "0.6fr 1.2fr 1.8fr 1.5fr 0.8fr 0.6fr 0.6fr",
            background: "var(--ups-muted)",
            borderColor: "var(--ups-border)",
          }}
        >
          <div>Decision</div>
          <div>From</div>
          <div>Subject</div>
          <div>Job / Outcome</div>
          <div>Received</div>
          <div className="text-center"><Paperclip size={11} className="inline" /></div>
          <div className="text-right">Open</div>
        </div>
        {loading ? (
          <div className="p-6 text-sm text-[color:var(--ups-subtext)] flex items-center gap-2">
            <Loader2 size={14} className="animate-spin" /> Loading inbound log…
          </div>
        ) : filtered.length === 0 ? (
          <div className="p-6 text-sm text-[color:var(--ups-subtext)]" data-testid="intake-empty">
            {log.length === 0
              ? "No inbound emails yet. Once an inbound webhook fires, every parsed payload will appear here."
              : "No rows match this filter."}
          </div>
        ) : (
          filtered.map((row) => {
            const isOpen = openId === row.id;
            const triage = row.triage || null;
            return (
              <div key={row.id} data-testid={`intake-row-${row.id}`}>
                <div
                  className="grid text-sm py-3 px-4 border-b hover:bg-[color:var(--ups-muted)] cursor-pointer transition"
                  style={{
                    gridTemplateColumns: "0.6fr 1.2fr 1.8fr 1.5fr 0.8fr 0.6fr 0.6fr",
                    borderColor: "var(--ups-border)",
                  }}
                  onClick={() => setOpenId(isOpen ? null : row.id)}
                >
                  <div className="self-center">
                    <DecisionPill decision={row.decision} />
                  </div>
                  <div className="self-center min-w-0">
                    <div className="font-mono text-xs truncate" title={row.from}>
                      {row.from || "—"}
                    </div>
                    {row.is_forwarded && row.forwarded_by && (
                      <div className="text-[10px] text-[color:var(--ups-subtext)] flex items-center gap-1 mt-0.5">
                        <Forward size={10} /> via {row.forwarded_by}
                      </div>
                    )}
                  </div>
                  <div className="self-center min-w-0">
                    <div className="text-sm truncate" title={row.subject}>{row.subject || "—"}</div>
                    {triage?.summary && (
                      <div className="text-[10px] text-[color:var(--ups-subtext)] truncate mt-0.5" title={triage.summary}>
                        {triage.summary}
                      </div>
                    )}
                  </div>
                  <div className="self-center min-w-0">
                    {row.job_id ? (
                      <button
                        onClick={(e) => { e.stopPropagation(); openJobInTracker(row.job_id); }}
                        className="text-xs font-mono font-bold flex items-center gap-1 hover:underline"
                        data-testid={`intake-row-open-job-${row.id}`}
                      >
                        <ExternalLink size={11} />
                        {row.job_id.slice(0, 8)}…
                      </button>
                    ) : (
                      <span className="text-[11px] text-[color:var(--ups-subtext)]">
                        {(row.decision || "").startsWith("discarded") ? (
                          <span className="inline-flex items-center gap-1 text-red-700">
                            <AlertTriangle size={11} /> not routed
                          </span>
                        ) : "—"}
                      </span>
                    )}
                    {triage?.priority && (
                      <div className="mt-0.5">
                        <PriorityPill value={triage.priority} />
                      </div>
                    )}
                  </div>
                  <div className="self-center text-[11px] font-mono text-[color:var(--ups-subtext)]">
                    {fmtDate(row.created_at)}
                  </div>
                  <div className="self-center text-center text-xs font-mono">
                    {row.attachment_count > 0 ? row.attachment_count : "—"}
                  </div>
                  <div className="self-center text-right">
                    {isOpen ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
                  </div>
                </div>
                {isOpen && (
                  <div
                    className="px-6 py-4 border-b bg-[color:var(--ups-muted)] text-xs space-y-3"
                    style={{ borderColor: "var(--ups-border)" }}
                    data-testid={`intake-row-detail-${row.id}`}
                  >
                    <div className="grid grid-cols-2 gap-3">
                      <div>
                        <div className="overline">From</div>
                        <div className="font-mono">{row.from || "—"}</div>
                      </div>
                      <div>
                        <div className="overline">To / Cc</div>
                        <div className="font-mono">{(row.to || []).join(", ")}</div>
                        {(row.cc || []).length > 0 && (
                          <div className="font-mono text-[color:var(--ups-subtext)]">cc: {(row.cc || []).join(", ")}</div>
                        )}
                      </div>
                      {row.is_forwarded && (
                        <div>
                          <div className="overline">Forwarded by</div>
                          <div className="font-mono">{row.forwarded_by || "—"} ({row.via || "—"})</div>
                        </div>
                      )}
                      <div>
                        <div className="overline">Message-id</div>
                        <div className="font-mono text-[10px] break-all">{row.message_id || "—"}</div>
                      </div>
                    </div>

                    {triage && (
                      <div className="card-swiss p-3 rounded-sm bg-white">
                        <div className="overline">LLM triage</div>
                        <div className="mt-1 text-xs space-y-1">
                          {triage.summary && <div><span className="font-bold">Summary: </span>{triage.summary}</div>}
                          {triage.priority && <div><span className="font-bold">Priority: </span>{triage.priority}</div>}
                          {triage.due_at && <div><span className="font-bold">Due: </span>{fmtDate(triage.due_at)}</div>}
                          {triage.reply_intent && <div><span className="font-bold">Reply intent: </span>{triage.reply_intent}</div>}
                          {triage.status_move && <div><span className="font-bold">Status move: </span>{triage.status_move}</div>}
                          {triage.products && triage.products.length > 0 && (
                            <div><span className="font-bold">Products: </span>{triage.products.join(", ")}</div>
                          )}
                        </div>
                      </div>
                    )}

                    {row.text_preview && (
                      <div>
                        <div className="overline">Text preview</div>
                        <pre
                          className="font-mono text-[11px] whitespace-pre-wrap bg-white p-2 rounded-sm max-h-48 overflow-auto"
                          style={{ border: "1px solid var(--ups-border)" }}
                        >
                          {row.text_preview}
                        </pre>
                      </div>
                    )}
                  </div>
                )}
              </div>
            );
          })
        )}
      </div>
    </div>
  );
};

export default EmailIntakeTab;
